import React, { useMemo, useState } from 'react';
import { Button, Card, DatePicker, Form, Select, Space, Table, Typography, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import api from '../../config/api';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;

type LogRow = {
  id: string;
  created_at: string;
  business_name?: string;
  agent_name?: string;
  ticket_number?: string | null;
  ticket_id?: string | null;
  payload: Record<string, unknown>;
};

export const RetainIQReports: React.FC = () => {
  const [form] = Form.useForm();
  const [params, setParams] = useState<Record<string, string>>(() => ({
    page: '1',
    limit: '50',
    created_from: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    created_to: dayjs().format('YYYY-MM-DD'),
  }));

  const { data, isLoading, refetch } = useQuery(
    ['retain-iq-reports', params],
    async () => {
      const res = await api.get('/api/retain-iq/reports', { params });
      return res.data as { logs: LogRow[]; total: number; page: number; limit: number };
    },
    { keepPreviousData: true }
  );

  const { data: agentsData } = useQuery('retain-iq-agents-reports', () =>
    api.get('/api/retain-iq/agents').then((r) => r.data)
  );
  const agents = agentsData?.agents || [];

  const columns: ColumnsType<LogRow> = useMemo(
    () => [
      { title: 'Date', dataIndex: 'created_at', key: 'created_at', width: 180, render: (t: string) => dayjs(t).format('YYYY-MM-DD HH:mm') },
      { title: 'Business', dataIndex: 'business_name', key: 'business_name', ellipsis: true },
      { title: 'Agent', dataIndex: 'agent_name', key: 'agent_name', width: 160 },
      {
        title: 'Call type',
        key: 'call_type',
        width: 200,
        ellipsis: true,
        render: (_: unknown, row) => String(row.payload?.call_type || '—'),
      },
      {
        title: 'Outcome',
        key: 'outcome',
        width: 160,
        ellipsis: true,
        render: (_: unknown, row) => String(row.payload?.call_outcome || '—'),
      },
      {
        title: 'Risk',
        key: 'risk',
        width: 100,
        render: (_: unknown, row) => String(row.payload?.risk_level || '—'),
      },
      { title: 'Ticket', dataIndex: 'ticket_number', key: 'ticket_number', width: 120, render: (n: string | null) => n || '—' },
    ],
    []
  );

  const onSearch = (vals: { range?: [dayjs.Dayjs, dayjs.Dayjs]; agent_user_id?: string; call_type?: string; has_ticket?: string; q?: string }) => {
    setParams((prev) => {
      const next: Record<string, string> = { ...prev, page: '1' };
      delete next.created_from;
      delete next.created_to;
      delete next.agent_user_id;
      delete next.call_type;
      delete next.has_ticket;
      delete next.q;
      if (vals.range?.[0]) next.created_from = vals.range[0].format('YYYY-MM-DD');
      if (vals.range?.[1]) next.created_to = vals.range[1].format('YYYY-MM-DD');
      if (vals.agent_user_id) next.agent_user_id = vals.agent_user_id;
      if (vals.call_type) next.call_type = vals.call_type;
      if (vals.has_ticket) next.has_ticket = vals.has_ticket;
      if (vals.q) next.q = vals.q;
      return next;
    });
  };

  const exportCsv = async () => {
    const res = await api.get('/api/retain-iq/reports/export', {
      params: {
        created_from: params.created_from,
        created_to: params.created_to,
        business_id: params.business_id,
        agent_user_id: params.agent_user_id,
      },
      responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'retain_iq_report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <Typography.Title level={4}>RetainIQ reports</Typography.Title>
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="inline"
          onFinish={onSearch}
          initialValues={{ range: [dayjs().subtract(30, 'day'), dayjs()] }}
        >
          <Form.Item name="range" label="Date range">
            <RangePicker />
          </Form.Item>
          <Form.Item name="agent_user_id" label="Agent">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Any"
              style={{ minWidth: 200 }}
              options={agents.map((a: { id: string; name: string }) => ({ value: a.id, label: a.name }))}
            />
          </Form.Item>
          <Form.Item name="call_type" label="Call type">
            <Input placeholder="Contains" allowClear style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="has_ticket" label="Ticket">
            <Select
              allowClear
              placeholder="Any"
              style={{ width: 120 }}
              options={[
                { value: 'true', label: 'Has ticket' },
                { value: 'false', label: 'No ticket' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="q"
            label="Search"
            tooltip="Matches if the text appears anywhere in the saved call log (all fields) or in the business name."
          >
            <Input placeholder="Text in log or business name" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Apply
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                Refresh
              </Button>
              <Button icon={<DownloadOutlined />} onClick={exportCsv}>
                Export CSV
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
      <Table<LogRow>
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={data?.logs || []}
        scroll={{ x: 900 }}
        pagination={{
          current: data?.page || 1,
          pageSize: data?.limit || 50,
          total: data?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          onChange: (page, pageSize) => {
            setParams((prev) => ({ ...prev, page: String(page), limit: String(pageSize || 50) }));
          },
        }}
      />
    </div>
  );
};
